import { Injectable, OnDestroy } from '@angular/core'
import {
  Image,
  ImagePath,
  Job,
  JobAbstract,
  JobService as JobApiService,
  JobList,
} from '@sciper/api-client'

import {
  BehaviorSubject,
  first,
  Observable,
  of,
  Subject,
  takeUntil,
} from 'rxjs'
import { environment } from 'src/environments/environment'

import {
  AbstractRestService,
  RestServiceStatus,
} from '../abstract-rest/abstract-rest.service'
import { ImageService } from '../image/image.service'
import {
  DEFAULT_PAGE_SIZE,
  InterestedUsersResult,
  JobAbstractsResult,
  JobByUniversityResult,
  JobFilterOptions,
  JobPageOptions,
  JobsResult,
  TimeGroupedJobAbstracts,
} from './job.fixtures'

@Injectable({
  providedIn: 'root',
})
export class JobService extends AbstractRestService implements OnDestroy {
  private _destroySubject$: Subject<void> = new Subject<void>()

  jobAbstracts: BehaviorSubject<JobAbstractsResult> =
    new BehaviorSubject<JobAbstractsResult>(undefined)

  jobs: BehaviorSubject<JobsResult> = new BehaviorSubject<JobsResult>(undefined)
  job: BehaviorSubject<Job> = new BehaviorSubject<Job>(undefined)

  jobRecommendationsAbstracts: BehaviorSubject<JobAbstract[]> =
    new BehaviorSubject<JobAbstract[]>(undefined)
  jobRecommendations: BehaviorSubject<Job[]> = new BehaviorSubject<Job[]>(
    undefined
  )

  jobNonRecommendationsAbstracts: BehaviorSubject<JobAbstract[]> =
    new BehaviorSubject<JobAbstract[]>(undefined)
  jobNonRecommendations: BehaviorSubject<Job[]> = new BehaviorSubject<Job[]>(
    undefined
  )

  jobsByUniversity: BehaviorSubject<JobByUniversityResult> =
    new BehaviorSubject<JobByUniversityResult>(undefined)

  interestedUsers: BehaviorSubject<InterestedUsersResult> =
    new BehaviorSubject<InterestedUsersResult>(undefined)

  constructor(
    private apiService: JobApiService,
    private imageService: ImageService
  ) {
    super()
  }

  override ngOnDestroy() {
    super.ngOnDestroy()
    this._destroySubject$.next()
    this._destroySubject$.complete()
  }

  fetchJobList(
    filterOptions: JobFilterOptions = undefined,
    forceReload: boolean = false,
    resolveObservable: boolean = true,
    pageOptions: JobPageOptions = {
      page: 0,
      size: DEFAULT_PAGE_SIZE,
    }
  ): Observable<JobList> {
    this.status.next(RestServiceStatus.Loading)
    const observable = this.apiService
      .getJobList(
        undefined,
        filterOptions?.searchKey,
        filterOptions?.keywordList,
        filterOptions?.departmentIdList,
        pageOptions?.page ?? 0,
        pageOptions?.size ?? DEFAULT_PAGE_SIZE
      )
      .pipe(takeUntil(this._destroySubject$))

    if (resolveObservable) {
      observable.subscribe(
        (jobList) => {
          // cache in service with respect to lastFilterOptions
          const currentJobs = this.jobAbstracts.value?.jobAbstracts ?? []

          if (
            forceReload ||
            !this.compareFilterOptions(
              filterOptions,
              this.jobAbstracts.value?.filterOptions
            ) ||
            currentJobs.length < DEFAULT_PAGE_SIZE
          ) {
            // replace previous jobAbstracts due to new filterOptions or reloading page 0
            this.jobAbstracts.next({
              jobAbstracts: jobList.items,
              filterOptions,
            })
            this.jobs.next({ jobs: [], filterOptions })
          } else {
            // append to previous jobAbstracts
            const newJobs = jobList.items.filter(
              (newJob) =>
                currentJobs.findIndex((j) => j.jobId === newJob.jobId) < 0
            )
            currentJobs.push(...newJobs)
            this.jobAbstracts.next({
              jobAbstracts: currentJobs,
              filterOptions,
            })
          }
          this.status.next(RestServiceStatus.Success)
        },
        (error) => {
          if (this.isNetworkError(error)) {
            this.status.next(RestServiceStatus.NetworkError)
          } else {
            this.status.next(RestServiceStatus.Error)
          }
        }
      )
    }

    return observable
  }

  fetchJobRecommendations(
    forceReload: boolean = false,
    opposite: boolean = false,
    page: number = 0,
    size: number = DEFAULT_PAGE_SIZE
  ) {
    this.status.next(RestServiceStatus.Loading)
    this.apiService
      .recommendJobListForUser(undefined, undefined, page, size, opposite)
      .pipe(takeUntil(this._destroySubject$))
      .subscribe(
        (jobList) => {
          // cache in service with respect to lastFilterOptions
          const currentJobs = opposite
            ? (this.jobNonRecommendationsAbstracts.value ?? [])
            : (this.jobRecommendationsAbstracts.value ?? [])

          if (forceReload || currentJobs.length < DEFAULT_PAGE_SIZE) {
            // replace previous jobAbstracts due to new filterOptions or reloading page 0
            if (opposite) {
              this.jobNonRecommendationsAbstracts.next(jobList.items)
              this.jobNonRecommendations.next([])
            } else {
              this.jobRecommendationsAbstracts.next(jobList.items)
              this.jobRecommendations.next([])
            }
          } else {
            // append to previous jobAbstracts
            const newJobs = jobList.items.filter(
              (newJob) =>
                currentJobs.findIndex((j) => j.jobId === newJob.jobId) < 0
            )
            currentJobs.push(...newJobs)
            if (opposite) {
              this.jobNonRecommendationsAbstracts.next(currentJobs)
            } else {
              this.jobRecommendationsAbstracts.next(currentJobs)
            }
          }
          this.status.next(RestServiceStatus.Success)
        },
        (error) => {
          if (this.isNetworkError(error)) {
            this.status.next(RestServiceStatus.NetworkError)
          } else {
            this.status.next(RestServiceStatus.Error)
          }
        }
      )
  }

  fetchJob(
    jobId: string,
    forceReload: boolean = false,
    resolveObservable: boolean = true
  ): Observable<Job> {
    // return cached data if available
    const jobIndex = this.jobs.value?.jobs.findIndex((j) => j.jobId === jobId)
    if (!forceReload && jobIndex >= 0) {
      const requestedJob = this.jobs.value?.jobs[jobIndex]
      if (this.job.value !== requestedJob) {
        this.job.next(requestedJob)
      }
      return of(requestedJob)
    } else if (forceReload) {
      this.job.next(undefined)
    }

    // otherwise cache loaded data
    const observable = this.apiService
      .getJob(jobId)
      .pipe(takeUntil(this._destroySubject$))

    if (resolveObservable) {
      observable.subscribe((newJob) => {
        this.job.next(newJob)
        let newJobs = this.jobs.value?.jobs ?? []
        const jobIndex = newJobs.findIndex((j) => j.jobId === jobId)
        if (jobIndex >= 0) {
          newJobs[jobIndex] = newJob
        } else {
          newJobs.push(newJob)
        }

        this.jobs.next({
          jobs: newJobs,
          filterOptions: this.jobAbstracts.value?.filterOptions,
        })
      })
    }

    return observable
  }

  compareFilterOptions(a: JobFilterOptions, b: JobFilterOptions): boolean {
    return (
      a?.searchKey === b?.searchKey &&
      this.compareArrays(a?.keywordList, b?.keywordList) &&
      this.compareArrays(a?.departmentIdList, b?.departmentIdList)
    )
  }

  fetchJobListByUniversity(
    universityId: string,
    forceReload: boolean = false,
    pageOptions: JobPageOptions = {
      page: 0,
      size: DEFAULT_PAGE_SIZE,
    }
  ) {
    this.status.next(RestServiceStatus.Loading)
    this.apiService
      .getJobListByUniversity(
        universityId,
        pageOptions?.page ?? 0,
        pageOptions?.size ?? DEFAULT_PAGE_SIZE
      )
      .pipe(takeUntil(this._destroySubject$))
      .subscribe(
        (jobList) => {
          // cache in service with respect to lastFilterOptions
          const currentJobs = this.jobsByUniversity.value?.jobAbstracts ?? []

          if (
            forceReload ||
            universityId !== this.jobsByUniversity.value?.universityId ||
            currentJobs.length < DEFAULT_PAGE_SIZE
          ) {
            // replace previous jobsByUniversity due to new filterOptions or reloading page 0
            this.jobsByUniversity.next({
              jobAbstracts: jobList.items,
              universityId,
            })
          } else {
            // append to previous jobsByUniversity
            const newJobs = jobList.items.filter(
              (newJob) =>
                currentJobs.findIndex((j) => j.jobId === newJob.jobId) < 0
            )
            currentJobs.push(...newJobs)
            this.jobsByUniversity.next({
              jobAbstracts: currentJobs,
              universityId,
            })
          }
          this.status.next(RestServiceStatus.Success)
        },
        (error) => {
          if (this.isNetworkError(error)) {
            this.status.next(RestServiceStatus.NetworkError)
          } else {
            this.status.next(RestServiceStatus.Error)
          }
        }
      )
  }

  async createJob(
    job: Job,
    callback:
      | ((success: boolean, jobId: string | undefined) => void)
      | undefined = undefined
  ) {
    this.apiService
      .createJob(job, job.languageTag)
      .pipe(takeUntil(this._destroySubject$))
      .subscribe(
        (result: string) => {
          this.status.next(RestServiceStatus.Success)
          if (callback) {
            callback(true, result)
          }
        },
        (error) => {
          if (this.isNetworkError(error)) {
            this.status.next(RestServiceStatus.NetworkError)
          } else {
            this.status.next(RestServiceStatus.Error)
          }
          if (callback) {
            callback(false, undefined)
          }
        }
      )
  }

  async createJobWithImage(
    job: Job,
    imagePath: string,
    imageFile: File,
    callback: ((success: boolean) => void) | undefined = undefined
  ) {
    this.createOrUpdateJobWithImage(true, job, imagePath, imageFile, callback)
  }

  async updateJob(
    job: Job,
    callback: ((success: boolean) => void) | undefined = undefined
  ) {
    this.updateJobAndRemoveImages(job, [], callback)
  }

  async updateJobAndRemoveImages(
    job: Job,
    removeImages: Image[],
    callback: ((success: boolean) => void) | undefined = undefined
  ) {
    this.apiService
      .updateJob(job.jobId, job)
      .pipe(takeUntil(this._destroySubject$))
      .subscribe(
        (_) => {
          this.status.next(RestServiceStatus.Success)
          if (callback) {
            callback(true)
          }
          if (removeImages.length) {
            removeImages.forEach((image) => {
              this.apiService
                .deleteJobImage(job.jobId, image.imageId)
                .pipe(first())
                .subscribe((_) => {
                  // nothing to do
                })
            })
          }
        },
        (error) => {
          if (this.isNetworkError(error)) {
            this.status.next(RestServiceStatus.NetworkError)
          } else {
            this.status.next(RestServiceStatus.Error)
          }
          if (callback) {
            callback(false)
          }
        }
      )
  }

  async updateJobWithImage(
    job: Job,
    imagePath: string,
    imageFile: File,
    callback: ((success: boolean) => void) | undefined = undefined
  ) {
    this.createOrUpdateJobWithImage(false, job, imagePath, imageFile, callback)
  }

  private async createOrUpdateJobWithImage(
    create: boolean,
    job: Job,
    imagePath: string,
    imageFile: File,
    callback: ((success: boolean) => void) | undefined = undefined
  ) {
    this.imageService.createImage(
      imagePath,
      imageFile,
      (success: boolean, imagePath: ImagePath) => {
        if (success && imagePath) {
          const image: Image = {
            alt: job.title,
            url: environment.apiBasePath + imagePath.imagePath,
          }

          if (create) {
            this.createJob(job, (success: boolean, jobId: string) => {
              if (success && jobId) {
                this.createJobImage(image, jobId, callback)
              }
            })
          } else {
            this.updateJob(job, (success: boolean) => {
              if (success) {
                this.createJobImage(image, job.jobId, callback)
              }
            })
          }
        }
      }
    )
  }

  async deleteJobs(
    jobIds: string[],
    callback: ((success: boolean) => void) | undefined = undefined
  ) {
    jobIds.forEach((jobId) => {
      this.deleteJob(jobId, callback)
    })
  }

  async deleteJob(
    jobId: string,
    callback: ((success: boolean) => void) | undefined = undefined
  ) {
    this.apiService
      .deleteJob(jobId)
      .pipe(takeUntil(this._destroySubject$))
      .subscribe(
        (_) => {
          this.status.next(RestServiceStatus.Success)
          if (callback) {
            callback(true)
          }
        },
        (error) => {
          if (this.isNetworkError(error)) {
            this.status.next(RestServiceStatus.NetworkError)
          } else {
            this.status.next(RestServiceStatus.Error)
          }
          if (callback) {
            callback(false)
          }
        }
      )
  }

  async createJobImage(
    image: Image,
    jobId: string,
    callback: ((success: boolean) => void) | undefined = undefined
  ) {
    this.status.next(RestServiceStatus.Loading)
    this.apiService
      .createJobImage(jobId, image)
      .pipe(takeUntil(this._destroySubject$))
      .subscribe(
        (_) => {
          this.status.next(RestServiceStatus.Success)
          if (callback) {
            callback(true)
          }
        },
        (error) => {
          if (this.isNetworkError(error)) {
            this.status.next(RestServiceStatus.NetworkError)
          } else {
            this.status.next(RestServiceStatus.Error)
          }
          if (callback) {
            callback(false)
          }
        }
      )
  }

  async fetchInterestedUsers(jobId: string) {
    this.apiService
      .getJobInterestedUser(jobId)
      .pipe(takeUntil(this._destroySubject$))
      .subscribe(
        (users) => {
          this.status.next(RestServiceStatus.Success)
          this.interestedUsers.next({
            users,
            jobId,
          })
        },
        (error) => {
          if (this.isNetworkError(error)) {
            this.status.next(RestServiceStatus.NetworkError)
          } else {
            this.status.next(RestServiceStatus.Error)
          }
        }
      )
  }

  groupJobsByOpeningDate(jobs: JobAbstract[]): TimeGroupedJobAbstracts[] {
    const groupedJobs: Record<string, TimeGroupedJobAbstracts> = {}

    jobs.forEach((job) => {
      const date = new Date(job.openingDate)
      const key = `${date.getFullYear()}-${date.getMonth()}`

      if (!groupedJobs[key]) {
        groupedJobs[key] = { date, jobAbstracts: [] }
      }
      groupedJobs[key].jobAbstracts.push(job)
    })

    return Object.values(groupedJobs)
  }

  private compareArrays(a: any[], b: any[]): boolean {
    if (!a && !b) {
      return true
    }
    if (!a || !b) {
      return false
    }
    return (
      a.length === b.length && a.every((element, index) => element === b[index])
    )
  }
}
